import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, ChevronLeft, Zap, Database, Lock, Users, Eye, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="w-6 h-6 text-[#00F0FF]" />
            <span className="font-heading font-bold text-lg">MATCHSTAKE</span>
          </div>
          <Button onClick={() => navigate(-1)} variant="outline" className="btn-secondary">
            <ChevronLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8"
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-white/10">
            <div className="w-14 h-14 rounded-2xl bg-[#00FF94]/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#00FF94]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold">Privacy Policy</h1>
              <p className="text-white/50">Last updated: March 2025</p>
            </div>
          </div>

          <div className="space-y-8 text-white/70 leading-relaxed">
            {/* Introduction */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#00F0FF]" />
                1. Introduction
              </h2>
              <p className="mb-4">
                MatchStake is committed to protecting your privacy. This Privacy Policy explains how we 
                collect, use, disclose, and safeguard your information when you use our skill-based 
                competitive gaming platform.
              </p>
              <p>
                By using MatchStake, you consent to the data practices described in this policy. If you 
                do not agree with our policies, please do not use our platform.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-[#7000FF]" />
                2. Information We Collect
              </h2>
              
              <h3 className="font-semibold text-white mb-2 mt-4">Account Information</h3>
              <p className="mb-4">When you create an account, we collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Username and display name</li>
                <li>Email address</li>
                <li>Password (stored securely using industry-standard hashing)</li>
                <li>Profile information you choose to provide</li>
              </ul>

              <h3 className="font-semibold text-white mb-2">Gameplay Data</h3>
              <p className="mb-4">When you use our Platform, we collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Match history and results</li>
                <li>Win/loss records and statistics</li>
                <li>ELO rating and skill level selections</li>
                <li>Proof submissions (screenshots, images)</li>
                <li>In-platform communications</li>
              </ul>

              <h3 className="font-semibold text-white mb-2">Payment Information</h3>
              <p className="mb-4">For financial transactions, we collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Transaction history (deposits, withdrawals, entry fees)</li>
                <li>Wallet balance information</li>
                <li>Payment method details (processed securely through third-party payment processors)</li>
              </ul>
              <p className="text-sm text-white/50">
                Note: We do not store complete credit card numbers. Payment processing is handled by 
                our secure payment partners.
              </p>

              <h3 className="font-semibold text-white mb-2 mt-4">Technical Data</h3>
              <p className="mb-4">We automatically collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Access times and dates</li>
                <li>Pages viewed and actions taken</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#00F0FF]" />
                3. How We Use Your Information
              </h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and maintain our Platform services</li>
                <li>Process transactions and manage your wallet</li>
                <li>Match you with opponents for competitions</li>
                <li>Verify match results and resolve disputes</li>
                <li>Calculate and update your ELO rating and statistics</li>
                <li>Prevent fraud, cheating, and abuse</li>
                <li>Communicate with you about your account and matches</li>
                <li>Send notifications about platform updates (with your consent)</li>
                <li>Improve our Platform and develop new features</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            {/* Data Storage and Security */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#CCFF00]" />
                4. Data Storage and Security
              </h2>
              <p className="mb-4">
                <strong className="text-white">Storage:</strong> Your data is stored on secure servers 
                with industry-standard protection. We retain your information for as long as your account 
                is active or as needed to provide services and comply with legal obligations.
              </p>
              <p className="mb-4">
                <strong className="text-white">Security Measures:</strong> We implement appropriate 
                technical and organizational measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Secure password hashing</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
                <li>Secure payment processing through certified providers</li>
              </ul>
              <p className="mt-4">
                While we strive to protect your information, no method of transmission over the Internet 
                is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FFB800]" />
                5. Third-Party Services
              </h2>
              <p className="mb-4">We use the following third-party services:</p>
              
              <h3 className="font-semibold text-white mb-2">Authentication</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Google OAuth:</strong> If you sign in with Google, we receive your name and 
                email address from Google. Google's privacy policy applies to information they collect.</li>
              </ul>

              <h3 className="font-semibold text-white mb-2">Payment Processing</h3>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Payment Providers:</strong> We use secure payment processors to handle 
                deposits and withdrawals. These providers have their own privacy policies and security 
                measures.</li>
              </ul>

              <h3 className="font-semibold text-white mb-2">Analytics</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>We may use analytics services to understand how users interact with our Platform 
                and improve our services.</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">
                6. Information Sharing
              </h2>
              <p className="mb-4">We do not sell your personal information. We may share information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">With Other Users:</strong> Your username, statistics, 
                and match history may be visible to other users (e.g., on leaderboards, in match lobbies)</li>
                <li><strong className="text-white">With Service Providers:</strong> We share information 
                with third-party vendors who help us operate the Platform</li>
                <li><strong className="text-white">For Legal Reasons:</strong> We may disclose information 
                if required by law, court order, or to protect our rights and safety</li>
                <li><strong className="text-white">Business Transfers:</strong> In the event of a merger 
                or acquisition, user information may be transferred</li>
              </ul>
            </section>

            {/* User Rights */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#00FF94]" />
                7. Your Rights
              </h2>
              <p className="mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Access:</strong> Request a copy of the personal 
                information we hold about you</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate 
                or incomplete information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your account 
                and personal information (subject to legal retention requirements)</li>
                <li><strong className="text-white">Opt-out:</strong> Unsubscribe from marketing 
                communications at any time</li>
                <li><strong className="text-white">Data Portability:</strong> Request your data in a 
                portable format where technically feasible</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at matchstakesupport@gmail.com.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">
                8. Cookies and Tracking
              </h2>
              <p className="mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Keep you logged in to your account</li>
                <li>Remember your preferences</li>
                <li>Analyze Platform usage and performance</li>
                <li>Prevent fraud and enhance security</li>
              </ul>
              <p className="mt-4">
                You can control cookies through your browser settings, but disabling cookies may affect 
                Platform functionality.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">
                9. Children's Privacy
              </h2>
              <p>
                MatchStake is not intended for users under 18 years of age. We do not knowingly collect 
                personal information from children under 18. If we discover that we have collected 
                information from a child under 18, we will delete that information immediately.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4">
                10. Changes to This Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant 
                changes by posting the new policy on this page and updating the "Last updated" date. 
                Your continued use of the Platform after changes constitutes acceptance of the updated 
                policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#00F0FF]" />
                11. Contact Information
              </h2>
              <p className="mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="mb-2">
                  <strong className="text-white">Email:</strong>{' '}
                  <a href="mailto:matchstakesupport@gmail.com" className="text-[#00F0FF] hover:underline">
                    matchstakesupport@gmail.com
                  </a>
                </p>
                <p>
                  <strong className="text-white">Response Time:</strong> 24-48 hours
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20">
              <p className="text-white/80">
                By using MatchStake, you acknowledge that you have read and understood this Privacy 
                Policy and consent to the collection, use, and disclosure of your information as 
                described herein.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
